// Secure Workflow Data Service
// Handles secure storage and retrieval of encoded function data using reference IDs
// Prevents raw HEX data exposure in the frontend

import logger from '../utils/logger';

// API configuration
const API_BASE_URL = '/api';

// Types for secure workflow data
export interface SecureWorkflowReference {
    id: string;
    type: 'memer_function' | 'timelock_schedule' | 'multisig_execute' | 'timelock_execute';
    description: string;
    contractName: string;
    functionName: string;
    timestamp: string;
    expiresAt: string;
    status: 'active' | 'used' | 'expired';
}

export interface SecureWorkflowData {
    id: string;
    encryptedData: string;
    checksum: string;
    metadata: {
        step: number;
        contractAddress: string;
        contractName: string;
        functionName: string;
        workflowType: string;
        parameters?: { [key: string]: any };
    };
    expiresAt: string;
}

export interface WorkflowDataRequest {
    rawData: string;
    metadata: {
        step: number;
        contractAddress: string;
        contractName: string;
        functionName: string;
        workflowType: string;
        parameters?: { [key: string]: any };
    };
    expirationHours?: number;
}

export interface StorageResponse<T = any> {
    success: boolean;
    data?: T;
    id?: string;
    message?: string;
    error?: string;
}

class SecureWorkflowDataService {
    private apiUrl: string;

    constructor() {
        this.apiUrl = `${API_BASE_URL}/secure-workflow-data`;
    }

    // Generic API call method
    private async apiCall<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<StorageResponse<T>> {
        try {
            const response = await fetch(endpoint, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
                ...options,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `HTTP ${response.status}`);
            }

            return data;
        } catch (error) {
            logger.error('Secure workflow data API error:', error);
            throw error;
        }
    }

    // Store encoded function data securely and return reference ID
    async storeWorkflowData(request: WorkflowDataRequest): Promise<StorageResponse<SecureWorkflowReference>> {
        logger.log('🔐 Storing secure workflow data:', request.metadata.functionName);

        return this.apiCall(`${this.apiUrl}?action=store`, {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }

    // Retrieve raw data using reference ID (server-side only)
    async retrieveWorkflowData(referenceId: string): Promise<StorageResponse<string>> {
        logger.log('🔓 Retrieving workflow data for reference:', referenceId);

        return this.apiCall(`${this.apiUrl}?action=retrieve&id=${referenceId}`);
    }

    // Get reference information (safe for frontend display)
    async getWorkflowReference(referenceId: string): Promise<StorageResponse<SecureWorkflowReference>> {
        logger.log('📋 Getting workflow reference info:', referenceId);

        return this.apiCall(`${this.apiUrl}?action=reference&id=${referenceId}`);
    }

    // List all active workflow references
    async listActiveReferences(): Promise<StorageResponse<SecureWorkflowReference[]>> {
        logger.log('📋 Listing active workflow references');

        return this.apiCall(`${this.apiUrl}?action=list-active`);
    }

    // Mark reference as used (prevents reuse)
    async markReferenceUsed(referenceId: string): Promise<StorageResponse> {
        logger.log('✅ Marking reference as used:', referenceId);

        return this.apiCall(`${this.apiUrl}?action=mark-used&id=${referenceId}`, {
            method: 'POST',
        });
    }

    // Delete workflow data (cleanup)
    async deleteWorkflowData(referenceId: string): Promise<StorageResponse> {
        logger.log('🗑️ Deleting workflow data:', referenceId);

        return this.apiCall(`${this.apiUrl}?action=delete&id=${referenceId}`, {
            method: 'DELETE',
        });
    }

    // Cleanup expired references
    async cleanupExpiredReferences(): Promise<StorageResponse<{ deletedCount: number }>> {
        logger.log('🧹 Cleaning up expired references');

        return this.apiCall(`${this.apiUrl}?action=cleanup`, {
            method: 'POST',
        });
    }

    // Generate secure reference display value for UI
    generateReferenceDisplay(reference: SecureWorkflowReference): string {
        return `{{${reference.id}}}`;
    }

    // Generate descriptive display for UI
    generateDescriptiveDisplay(reference: SecureWorkflowReference): {
        displayValue: string;
        description: string;
        tooltip: string;
    } {
        const displayValue = `{{${reference.id}}}`;
        const description = `Secure reference to: ${reference.functionName}`;
        const tooltip = `🔒 Secure workflow data\n` +
            `Function: ${reference.contractName}.${reference.functionName}\n` +
            `Type: ${reference.type}\n` +
            `Created: ${new Date(reference.timestamp).toLocaleString()}\n` +
            `Expires: ${new Date(reference.expiresAt).toLocaleString()}`;

        return { displayValue, description, tooltip };
    }

    // Validate reference format
    isValidReference(value: string): boolean {
        return /^\{\{WF_\d{8}_\d{3}\}\}$/.test(value);
    }

    // Extract reference ID from display value
    extractReferenceId(displayValue: string): string | null {
        const match = displayValue.match(/^\{\{(WF_\d{8}_\d{3})\}\}$/);
        return match ? match[1] : null;
    }

    // Create workflow data for common scenarios
    async storeMemerFunctionData(
        functionData: string,
        contractAddress: string,
        contractName: string,
        functionName: string,
        parameters?: { [key: string]: any }
    ): Promise<StorageResponse<SecureWorkflowReference>> {
        return this.storeWorkflowData({
            rawData: functionData,
            metadata: {
                step: 1,
                contractAddress,
                contractName,
                functionName,
                workflowType: 'memer_to_timelock',
                parameters
            },
            expirationHours: 24
        });
    }

    async storeTimelockScheduleData(
        scheduleData: string,
        timelockAddress: string,
        originalFunctionRef: string,
        salt: string,
        delay: string
    ): Promise<StorageResponse<SecureWorkflowReference>> {
        return this.storeWorkflowData({
            rawData: scheduleData,
            metadata: {
                step: 2,
                contractAddress: timelockAddress,
                contractName: 'TimelockController',
                functionName: 'schedule',
                workflowType: 'memer_to_timelock',
                parameters: {
                    originalFunctionRef,
                    salt,
                    delay
                }
            },
            expirationHours: 48
        });
    }

    async storeMultiSigExecuteData(
        executeData: string,
        multiSigAddress: string,
        timelockRef: string
    ): Promise<StorageResponse<SecureWorkflowReference>> {
        return this.storeWorkflowData({
            rawData: executeData,
            metadata: {
                step: 3,
                contractAddress: multiSigAddress,
                contractName: 'MultiSigWallet',
                functionName: 'execTransaction',
                workflowType: 'memer_to_timelock',
                parameters: {
                    timelockRef
                }
            },
            expirationHours: 72
        });
    }

    // Health check
    async healthCheck(): Promise<boolean> {
        try {
            await this.listActiveReferences();
            return true;
        } catch (error) {
            logger.error('Secure workflow data service health check failed:', error);
            return false;
        }
    }

    // Fallback to sessionStorage if backend is unavailable
    private fallbackStore: { [key: string]: { data: string; reference: SecureWorkflowReference } } = {};

    async storeWorkflowDataFallback(request: WorkflowDataRequest, existingReferenceId?: string): Promise<StorageResponse<SecureWorkflowReference>> {
        logger.log('📦 Storing workflow data to sessionStorage (fallback)');

        // Use existing reference ID if provided, otherwise generate new one
        const referenceId = existingReferenceId || this.generateFallbackId();
        const reference: SecureWorkflowReference = {
            id: referenceId,
            type: request.metadata.workflowType as any,
            description: `${request.metadata.contractName}.${request.metadata.functionName}`,
            contractName: request.metadata.contractName,
            functionName: request.metadata.functionName,
            timestamp: new Date().toISOString(),
            expiresAt: new Date(Date.now() + (request.expirationHours || 24) * 60 * 60 * 1000).toISOString(),
            status: 'active'
        };

        this.fallbackStore[referenceId] = {
            data: request.rawData,
            reference
        };

        // Also store in sessionStorage for persistence across page reloads
        try {
            sessionStorage.setItem(`secure_workflow_${referenceId}`, JSON.stringify({
                data: request.rawData,
                reference
            }));
            logger.log('✅ Stored in sessionStorage with key:', `secure_workflow_${referenceId}`);
        } catch (error) {
            logger.warn('Failed to store in sessionStorage:', error);
        }

        return {
            success: true,
            data: reference,
            id: referenceId
        };
    }

    async retrieveWorkflowDataFallback(referenceId: string): Promise<StorageResponse<string>> {
        logger.log('📥 Retrieving workflow data from fallback storage:', referenceId);

        // Try memory first
        if (this.fallbackStore[referenceId]) {
            return {
                success: true,
                data: this.fallbackStore[referenceId].data
            };
        }

        // Try sessionStorage
        try {
            const stored = sessionStorage.getItem(`secure_workflow_${referenceId}`);
            if (stored) {
                const parsed = JSON.parse(stored);
                return {
                    success: true,
                    data: parsed.data
                };
            }
        } catch (error) {
            logger.warn('Failed to retrieve from sessionStorage:', error);
        }

        return {
            success: false,
            error: 'Reference not found in fallback storage'
        };
    }

    private generateFallbackId(): string {
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `WF_${date}_${sequence}`;
    }
}

// Export singleton instance
export const secureWorkflowDataService = new SecureWorkflowDataService();
export default secureWorkflowDataService;
